import { useId } from 'react';
import { Button, Text, makeStyles } from '@fluentui/react-components';
import { ShellIcons, StatusDot, tokens } from '@vigeo/ui';

const useStyles = makeStyles({
  card: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
  },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
    gap: tokens.spacingVerticalM, paddingBlock: tokens.spacingVerticalXXL, paddingInline: tokens.spacingHorizontalL,
  },
  icon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: tokens.spacingHorizontalXXXL, height: tokens.spacingHorizontalXXXL,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground2,
  },
  title: { margin: 0, fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500, fontWeight: tokens.fontWeightSemibold },
  actions: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalS },
  stage: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalM, paddingInline: tokens.spacingHorizontalL,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

/**
 * Offer and stage for a candidate with no offer on record (#429, frame 945:13233).
 *
 * One primary action. Recording an offer signed on paper is the less common path, so it is the
 * quieter button. A viewer gets the message and the stage line without either action.
 */
export function NoOfferYet({ canEdit, busy, onDraft, onRecordPaper }: {
  readonly canEdit: boolean;
  readonly busy: boolean;
  readonly onDraft: () => void;
  readonly onRecordPaper: () => void;
}) {
  const styles = useStyles();
  const headingId = useId();
  return (
    <section className={styles.card} aria-labelledby={headingId}>
      <div className={styles.empty}>
        <span className={styles.icon}><ShellIcons.document /></span>
        <h3 id={headingId} className={styles.title}>No offer yet</h3>
        <Text>Start from the approved offer letter (PDF).</Text>
        {canEdit ? (
          <div className={styles.actions}>
            <Button appearance="primary" size="large" disabled={busy} onClick={onDraft}>Draft the offer</Button>
            <Button appearance="subtle" size="large" disabled={busy} onClick={onRecordPaper}>Record a paper-signed offer</Button>
          </div>
        ) : null}
      </div>
      <div className={styles.stage}>
        <Text weight="semibold">Stage</Text>
        <StatusDot color={tokens.colorNeutralForeground3}>Candidate</StatusDot>
        <Text size={200}>Moves to Onboarding after a signed offer.</Text>
      </div>
    </section>
  );
}
